import styles from "./JigsawGame.module.less";
import DownTime from "../components/DownTime";
import NoticeText from "../components/NoticeText";
import { useState, useEffect, useRef } from "react";
import DSBridge from "dsbridge";
import { getGameAudio, getMaterialsByGame } from "@/api/mine";
import { showDeviceModal } from "views/mine/device-manage/components/device-modal";
import { Toast } from "react-vant";
import { useSearchParams } from "react-router-dom";

const JigsawGame = () => {
  const [searchParams] = useSearchParams();
  const [isVisible, setIsVisible] = useState(true);
  const [title, setTitle] = useState("");
  const [puzzlePieces, setPuzzlePieces] = useState([]);
  const [placedPieces, setPlacedPieces] = useState([]); // 右侧已放置的拼图
  const [showHand, setShowHand] = useState(true);
  const downTimeRef = useRef(null);
  const isStartRef = useRef(false);
  const containerRef = useRef(null);
  const explanationRef = useRef(null);

  // 拖拽相关的ref
  const isDraggingRef = useRef(false);
  const draggedPieceRef = useRef(null);
  const draggedElementRef = useRef(null);
  const startPositionRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef(null);

  // 打乱数组顺序的函数
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const handleClose = () => {
    showDeviceModal({
      message:
        "确定退出课堂练习？中途退出无法获得积分哦~退出后将继续学习课程。",
      onConfirm: () => {
        DSBridge.call("stopSound", "bgm_sound");
        DSBridge.call("exitWebView");
      },
      isShowCancel: true,
      isShowConfirm: true,
      cancelText: "取消",
      confirmText: "退出",
    });
  };

  const handleImageCut = (imageUrl) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageUrl;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const size = Math.min(image.width, image.height);
      canvas.width = size;
      canvas.height = size;

      ctx.drawImage(image, 0, 0, size, size);

      const pieceSize = size / 2;
      const pieces = [];

      for (let i = 0; i < 4; i++) {
        const row = Math.floor(i / 2);
        const col = i % 2;

        const pieceCanvas = document.createElement("canvas");
        pieceCanvas.width = pieceSize;
        pieceCanvas.height = pieceSize;
        const pieceCtx = pieceCanvas.getContext("2d");

        pieceCtx.drawImage(
          canvas,
          col * pieceSize,
          row * pieceSize,
          pieceSize,
          pieceSize,
          0,
          0,
          pieceSize,
          pieceSize
        );

        pieces.push({
          id: i,
          correctPosition: i, // 记录正确位置
          imageUrl: pieceCanvas.toDataURL(),
          isPlaced: false, // 是否已被放置
        });
      }

      // 打乱拼图顺序
      const shuffledPieces = shuffleArray(pieces);
      setPuzzlePieces(shuffledPieces);

      // 初始化右侧容器为空数组，长度为4
      setPlacedPieces(new Array(4).fill(null));
    };
  };

  // 获取触摸点相对于容器的位置
  const getTouchPosition = (touch) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  };

  // 检查拖拽是否在右侧容器内
  const isInRightContainer = (x, y) => {
    const rightContainer = document.querySelector(
      `.${styles["center-right"]} .${styles["puzzle-content"]}`
    );
    if (!rightContainer || !containerRef.current)
      return { inContainer: false, position: -1 };

    const rect = rightContainer.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const relativeX = x - (rect.left - containerRect.left);
    const relativeY = y - (rect.top - containerRect.top);

    if (
      relativeX >= 0 &&
      relativeX <= rect.width &&
      relativeY >= 0 &&
      relativeY <= rect.height
    ) {
      // 计算在2x2网格中的位置
      const col = Math.floor(relativeX / (rect.width / 2));
      const row = Math.floor(relativeY / (rect.height / 2));
      const position = row * 2 + col;

      return { inContainer: true, position: Math.min(position, 3) };
    }

    return { inContainer: false, position: -1 };
  };

  // 更新拖拽元素位置（直接操作DOM）
  const updateDragPosition = (x, y) => {
    if (draggedElementRef.current && startPositionRef.current) {
      const deltaX = x - startPositionRef.current.x;
      const deltaY = y - startPositionRef.current.y;

      draggedElementRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(1.1)`;
      draggedElementRef.current.style.zIndex = "1000";
    }
  };

  // 开始拖拽
  const handleTouchStart = (e, piece) => {
    if (!isStartRef.current) return;
    if (piece.isPlaced) return;

    setShowHand(false);
    isDraggingRef.current = true;
    draggedPieceRef.current = piece;
    draggedElementRef.current = e.currentTarget;

    const touch = e.touches[0];
    const touchPos = getTouchPosition(touch);
    startPositionRef.current = { x: touchPos.x, y: touchPos.y };

    // 立即应用拖拽样式
    if (draggedElementRef.current) {
      draggedElementRef.current.style.transform = "scale(1.1)";
      draggedElementRef.current.style.zIndex = "1000";
      draggedElementRef.current.style.transition = "none";
    }
  };

  // 拖拽移动
  const handleTouchMove = (e) => {
    if (!isStartRef.current) return;
    if (!isDraggingRef.current || !draggedElementRef.current) return;

    const touch = e.touches[0];
    const touchPos = getTouchPosition(touch);

    // 取消之前的动画帧
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // 使用requestAnimationFrame来优化性能
    animationFrameRef.current = requestAnimationFrame(() => {
      updateDragPosition(touchPos.x, touchPos.y);
    });
  };

  // 结束拖拽
  const handleTouchEnd = (e) => {
    if (!isStartRef.current) {
      isDraggingRef.current = false;
      draggedPieceRef.current = null;
      draggedElementRef.current = null;
      return;
    }

    if (
      !isDraggingRef.current ||
      !draggedPieceRef.current ||
      !draggedElementRef.current
    ) {
      // 重置状态
      isDraggingRef.current = false;
      draggedPieceRef.current = null;
      draggedElementRef.current = null;
      return;
    }

    const touch = e.changedTouches[0];
    const touchPos = getTouchPosition(touch);
    const { inContainer, position } = isInRightContainer(
      touchPos.x,
      touchPos.y
    );

    const currentPiece = draggedPieceRef.current;
    const currentElement = draggedElementRef.current;

    if (
      inContainer &&
      position === currentPiece.correctPosition &&
      !placedPieces[position]
    ) {
      // 位置正确且该位置未被占用
      const newPlacedPieces = [...placedPieces];
      newPlacedPieces[position] = currentPiece;
      setPlacedPieces(newPlacedPieces);

      // 从左侧移除该拼图
      setPuzzlePieces((prev) =>
        prev.map((piece) =>
          piece.id === currentPiece.id ? { ...piece, isPlaced: true } : piece
        )
      );

      // 检查是否完成拼图
      if (newPlacedPieces.filter((piece) => piece !== null).length === 4) {
        console.log("拼图完成！");
      }
    } else {
      // 位置错误，恢复原位置
      if (currentElement) {
        currentElement.style.transform = "none";
        currentElement.style.transition = "transform 0.3s ease";
      }
    }

    // 重置拖拽状态
    if (currentElement) {
      currentElement.style.zIndex = "auto";
      setTimeout(() => {
        if (currentElement && currentElement.style) {
          currentElement.style.transition = "";
        }
      }, 300);
    }

    isDraggingRef.current = false;
    draggedPieceRef.current = null;
    draggedElementRef.current = null;
    startPositionRef.current = { x: 0, y: 0 };

    // 清理动画帧
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // 全局事件监听器
  useEffect(() => {
    const handleGlobalTouchMove = (e) => {
      if (isDraggingRef.current && isStartRef.current) {
        e.preventDefault();
        handleTouchMove(e);
      }
    };

    const handleGlobalTouchEnd = (e) => {
      if (isDraggingRef.current && isStartRef.current) {
        e.preventDefault();
        handleTouchEnd(e);
      }
    };

    // 添加全局事件监听器，设置为非被动模式
    document.addEventListener("touchmove", handleGlobalTouchMove, {
      passive: false,
    });
    document.addEventListener("touchend", handleGlobalTouchEnd, {
      passive: false,
    });

    return () => {
      document.removeEventListener("touchmove", handleGlobalTouchMove);
      document.removeEventListener("touchend", handleGlobalTouchEnd);

      // 清理动画帧
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [handleTouchMove, handleTouchEnd]);

  const handleTimeEnd = async () => {
    DSBridge.call("stopSound", "bgm_sound");

    // 如果有正在拖拽的元素，恢复其状态
    if (isDraggingRef.current && draggedElementRef.current) {
      const currentElement = draggedElementRef.current;
      currentElement.style.transform = "none";
      currentElement.style.zIndex = "auto";
      currentElement.style.transition = "transform 0.3s ease";

      setTimeout(() => {
        if (currentElement && currentElement.style) {
          currentElement.style.transition = "";
        }
      }, 300);
    }

    // 重置所有拖拽相关的状态
    isDraggingRef.current = false;
    draggedPieceRef.current = null;
    draggedElementRef.current = null;
    startPositionRef.current = { x: 0, y: 0 };

    // 清理动画帧
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // 设置游戏状态为结束
    isStartRef.current = false;

    // 然后根据之前的完成情况决定动画
    const hadAnyPieces = placedPieces.findIndex((item) => item !== null) !== -1;

    if (hadAnyPieces) {
      DSBridge.call("playSVGA", "nice_try", async () => {
        autoCompletePuzzleWithAnimation().then(() => {
          playAudioAndExit(explanationRef.current?.incorrect, 5);
        });
      });
    } else {
      setShowHand(false);
      DSBridge.call("playSVGA", "missed", async () => {
        autoCompletePuzzleWithAnimation().then(() => {
          playAudioAndExit(explanationRef.current?.noAnswer);
        });
      });
    }
  };

  const handleGetAudio = async (text) => {
    try {
      // const res = await getGameAudio({
      //   character_id: "lily_white",
      //   token: "IMdW6ZWukrH6qrUteWDa6Mzm-SR-zPxW3z1Iq-2xg08.",
      //   text: text,
      // });
      const res = await getGameAudio({
        character_id: searchParams.get("character_id"),
        token: searchParams.get("token"),
        text: text,
      });
      if (res.code === 200 && res.data?.url) {
        return res.data.url;
      }
      throw new Error("Failed to get audio URL");
    } catch (error) {
      throw error;
    }
  };

  // 提取播放音频和退出的公共逻辑
  const playAudioAndExit = async (explanationText, score = 0) => {
    try {
      const audioUrl = await handleGetAudio(explanationText);
      DSBridge.call(
        "playOnlineSound",
        `${process.env.REACT_APP_API_CDN}/${audioUrl}`,
        () => {
          if (score > 0) {
            DSBridge.call("addScore", score.toString());
          }
          DSBridge.call("exitWebView");
        }
      );
    } catch {
      if (score > 0) {
        DSBridge.call("addScore", score.toString());
      }
      DSBridge.call("exitWebView");
    }
  };

  const initGame = async () => {
    try {
      // const res = {
      //   code: 200,
      //   status: true,
      //   data: {
      //     type: 14,
      //     question: "It's practice time!",
      //     question_zh: "",
      //     answer: "",
      //     image:
      //       "https://cdn03.myaitalk.vip/utalkrc/materials/slides/game_time_slides/game_1.jpg",
      //     practice_config: {
      //       type: "puzzle",
      //       guide: "Complete the puzzle. What word do you see",
      //       duration: 10,
      //       explanation: {
      //         correct:
      //           "Great job! The word is afternoon. It means the time of day between noon and evening.",
      //         incorrect:
      //           "Oops! The correct word is afternoon. It refers to the time between 12 p.m. and the early evening.",
      //         noAnswer:
      //           "The word we were looking for is afternoon. It's the part of the day after lunch and before sunset.",
      //       },
      //       title: "Put the pieces together!",
      //       config: {
      //         image:
      //           "https://cdn03.myaitalk.vip/utalkrc/materials/slides/test_test/test_test-unit_1_tts_test_111301/practice_images/material_practice_image_504a00c8-3f76-4237-bd29-f7b36072c9f4.png",
      //       },
      //     },
      //   },
      //   message: "success",
      // };
      const res = await getMaterialsByGame({
        material_id: searchParams.get("material_id"),
        token: searchParams.get("token"),
        unit_id: searchParams.get("unit_id"),
      });
      if (res.code === 200) {
        const config = res.data?.practice_config;
        if (config?.type !== "puzzle") {
          Toast.info("游戏类型错误，请点击左上角退出");
          return;
        }
        explanationRef.current = config?.explanation;
        setTitle(config?.title || "");
        handleImageCut(config?.config?.image);
        try {
          const audioUrl = await handleGetAudio(config?.guide);
          DSBridge.call(
            "playOnlineSound",
            `${process.env.REACT_APP_API_CDN}/${audioUrl}`,
            () => {
              downTimeRef.current?.startCountdown(config?.duration);
              isStartRef.current = true;
            }
          );
        } catch {
          downTimeRef.current?.startCountdown(config?.duration);
          isStartRef.current = true;
        }
      } else {
        Toast.info("请求游戏数据失败，请点击左上角退出");
      }
    } catch {
      Toast.info("验证游戏数据失败，请点击左上角退出");
    }
  };
  useEffect(() => {
    if (
      isStartRef.current &&
      placedPieces.findIndex((item) => item === null) === -1
    ) {
      DSBridge.call("stopSound", "bgm_sound");
      downTimeRef.current.stopCountdown();
      isStartRef.current = false;
      DSBridge.call("playSVGA", "outstanding", () => {
        playAudioAndExit(explanationRef.current?.correct, 10);
      });
    }
  }, [placedPieces]);
  useEffect(() => {
    DSBridge.call("playSound", "bgm_sound");
    DSBridge.call("playSound", "start_sound");
    setTimeout(() => {
      setIsVisible(false);
    }, 1000);
    initGame();
    return () => {
      DSBridge.call("stopSound", "bgm_sound");
    };
  }, []);

  const autoCompletePuzzleWithAnimation = () => {
    return new Promise((resolve) => {
      const unplacedPieces = puzzlePieces.filter((piece) => !piece.isPlaced);

      if (unplacedPieces.length === 0) {
        resolve();
        return;
      }

      let currentIndex = 0;

      const placeNextPiece = () => {
        if (currentIndex >= unplacedPieces.length) {
          resolve();
          return;
        }

        const piece = unplacedPieces[currentIndex];

        // 更新 placedPieces
        setPlacedPieces((prev) => {
          const newPlaced = [...prev];
          newPlaced[piece.correctPosition] = piece;
          return newPlaced;
        });

        // 从左侧移除
        setPuzzlePieces((prev) =>
          prev.map((p) => (p.id === piece.id ? { ...p, isPlaced: true } : p))
        );

        currentIndex++;

        // 延迟放置下一个片段，创造动画效果
        setTimeout(placeNextPiece, 300);
      };

      placeNextPiece();
    });
  };

  return (
    <div className={styles["jigsaw-game-container"]} ref={containerRef}>
      {isVisible && (
        <div className={styles["start-box"]}>
          <img
            src={require("@/images/new-game/common/start.png")}
            className={styles["start-cover"]}
          />
        </div>
      )}
      <div className={`${isVisible ? styles["visibility-box"] : ""}`}>
        <div className={styles["back-top"]}></div>
        <div className={styles["back-bottom"]}></div>
        <div className={styles["title"]}>
          <div className={styles["title-text"]}>{title}</div>
        </div>
        <div className={styles["center"]}>
          <div className={styles["center-left"]}>
            <div className={styles["puzzle-wrapper"]}>
              <div className={styles["puzzle-content"]}>
                {puzzlePieces.map(
                  (piece, index) =>
                    !piece.isPlaced && (
                      <div
                        key={`${piece.id}-${index}`}
                        className={styles["puzzle-piece"]}
                        style={{
                          backgroundImage: `url(${piece.imageUrl})`,
                        }}
                        onTouchStart={(e) => handleTouchStart(e, piece)}
                      />
                    )
                )}
              </div>
            </div>
          </div>
          <div className={styles["center-right"]}>
            <div className={styles["puzzle-wrapper"]}>
              <div className={styles["puzzle-content"]}>
                {placedPieces.map((piece, index) => (
                  <div
                    key={`placed-${index}`}
                    className={styles["puzzle-piece"]}
                    style={{
                      backgroundImage: piece
                        ? `url(${piece.imageUrl})`
                        : "none",
                      opacity: piece ? 1 : 0.3,
                      border: piece
                        ? "none"
                        : "2px dashed rgba(255, 255, 255, 0.5)",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
          {showHand && (
            <img
              src={require("@/images/new-game/jigsaw-game/hand.png")}
              className={styles["hand"]}
            />
          )}
        </div>
        <NoticeText text="Drag  来回拖拽，完成拼图哦" />
        <DownTime ref={downTimeRef} onTimeEnd={() => handleTimeEnd()} />
      </div>
      <div className={styles["close-icon"]} onClick={handleClose}>
        <img
          className={styles["close-icon-img"]}
          src={require("@/images/new-game/common/close.png")}
        />
      </div>
    </div>
  );
};

export default JigsawGame;
